import type { BaselineRepresentativeness } from '../baselines/schema';
import { ACUITY_RULE_VERSION, type AcuityAssessmentReasonCode, type AcuityBand } from './acuity';

/** Drinks on one day that count as a binge day for the interim proxy (5+ standard drinks). */
export const INTERIM_PROXY_BINGE_DAY_DRINKS = 5;

export type RegistrationAcuityWeekInput = {
  complete: boolean;
  representativeness: BaselineRepresentativeness;
  entries: ReadonlyArray<{ quantity: number | null; used: boolean }>;
};

export type RegistrationAcuityInput = {
  programmeKey: string;
  week: RegistrationAcuityWeekInput | null;
  taps2AlcoholConcern: 'yes' | 'no' | undefined;
};

export type RegistrationAcuityResult = {
  band: AcuityBand;
  reason: AcuityAssessmentReasonCode | null;
  ruleVersion: typeof ACUITY_RULE_VERSION;
};

function drinkCount(entry: RegistrationAcuityWeekInput['entries'][number]): number {
  return entry.used ? (entry.quantity ?? 0) : 0;
}

/** A drinking day with no amount cannot rule out a binge, so it must never read as zero. */
function hasUnquantifiedUse(week: RegistrationAcuityWeekInput): boolean {
  return week.entries.some((entry) => entry.used && entry.quantity === null);
}

/**
 * Interim alcohol registration acuity from the designated baseline week and TAPS-2.
 * Clinical sign-off on this proxy is tracked in the spec (Q34); not the Pelago 1.0 PQ rule.
 */
export function deriveRegistrationAcuity(input: RegistrationAcuityInput): RegistrationAcuityResult {
  const base = { ruleVersion: ACUITY_RULE_VERSION };
  if (input.programmeKey !== 'alcohol') {
    return { ...base, band: 'unknown', reason: 'programme_not_alcohol' };
  }
  const week = input.week;
  if (!week) {
    return { ...base, band: 'unknown', reason: 'no_baseline' };
  }
  if (!week.complete) {
    return { ...base, band: 'unknown', reason: 'week_incomplete' };
  }
  if (week.representativeness !== 'confirmed') {
    return { ...base, band: 'unknown', reason: 'week_not_representative' };
  }
  if (hasUnquantifiedUse(week)) {
    return { ...base, band: 'unknown', reason: 'week_incomplete' };
  }

  const hasBingeDay = week.entries.some(
    (entry) => drinkCount(entry) >= INTERIM_PROXY_BINGE_DAY_DRINKS,
  );
  if (hasBingeDay) {
    return { ...base, band: 'high', reason: 'binge_day' };
  }

  if (input.taps2AlcoholConcern === 'yes') {
    return { ...base, band: 'high', reason: 'taps2_positive' };
  }
  if (input.taps2AlcoholConcern === undefined) {
    return { ...base, band: 'unknown', reason: 'taps2_unanswered' };
  }

  const allZero = week.entries.every((entry) => drinkCount(entry) === 0);
  if (allZero) {
    return { ...base, band: 'none', reason: 'zero_drinks' };
  }

  return { ...base, band: 'low', reason: 'low_drinks' };
}
